import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Loader2, Package, Minus, Plus, Shield, Heart, Truck } from 'lucide-react';
import axios from 'axios';
import { MerchTransparencyNotice } from './components/MerchTransparencyNotice';
import ImageLightbox from '../components/ImageLightbox';
import { isValidEmail, getEmailError } from '../utils/emailValidation';
import { useImageDisplayConfig } from '../hooks/useSiteContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductPage = () => {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [lightboxImg, setLightboxImg] = useState(null);
  const imgConfig = useImageDisplayConfig();

  // Shipping form
  const [shipping, setShipping] = useState({
    name: '', email: '', phone: '', address: '', apartment: '',
    city: '', state: '', zip: '', country: 'US', notes: ''
  });
  const [showCheckout, setShowCheckout] = useState(false);
  const [processing, setProcessing] = useState(false);

  const fetchItem = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/shop/items/${itemId}`);
      setItem(res.data);
      // Initialize selected options. For size-tracked apparel, default to the
      // first IN-STOCK size so the checkout flow never starts on a sold-out
      // option. For other variations, default to the first listed option.
      const opts = {};
      const sizeSummary = res.data.size_summary;
      if (sizeSummary?.sizes?.length) {
        const firstInStock = sizeSummary.sizes.find(s => s.in_stock);
        if (firstInStock) opts.Size = firstInStock.size;
      }
      (res.data.variations || []).forEach(v => {
        // Skip the "Size" variation when per-size stock is the source of
        // truth — we render that selector separately below.
        if (sizeSummary?.sizes?.length && v.name?.trim().toLowerCase() === 'size') return;
        if (v.options?.length > 0) opts[v.name] = v.options[0];
      });
      setSelectedOptions(opts);
    } catch {
      setItem(null);
    }
    setLoading(false);
  }, [itemId]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  const handleCheckout = async () => {
    if (!shipping.name.trim() || !shipping.email.trim() || !shipping.address.trim() ||
        !shipping.city.trim() || !shipping.state.trim() || !shipping.zip.trim()) {
      alert('Please fill in all required shipping fields.');
      return;
    }
    const emailErr = getEmailError(shipping.email);
    if (emailErr) { alert(emailErr); return; }
    setProcessing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/shop/checkout`, {
        cart_items: [{ item_id: itemId, quantity, selected_options: selectedOptions }],
        origin_url: window.location.origin,
        shipping,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Checkout failed. Please try again.');
    }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center px-6 text-center">
        <Package className="w-12 h-12 text-[#D1D5DB] mb-3" />
        <p className="text-lg font-semibold text-[#0A0A0A] mb-2">Product Not Found</p>
        <Link to="/shop" className="btn-primary text-sm"><ArrowLeft className="w-4 h-4" /> Back to Shop</Link>
      </div>
    );
  }

  const maxQty = item.remaining || 20;
  const variations = item.variations || [];

  return (
    <>
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <div className="bg-[#0A0A0A] py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/shop" className="text-gray-400 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4" /> Shop
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Product Images */}
          <div>
            {item.image ? (
              <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setLightboxImg(item.image)} data-testid="product-main-image">
                <img src={item.image} alt={item.name} className="w-full aspect-square object-cover" />
              </div>
            ) : imgConfig.hide_empty_product_images ? null : (
              <div className="bg-white border border-[#E5E7EB] rounded-xl aspect-square flex items-center justify-center">
                <Package className="w-16 h-16 text-[#D1D5DB]" />
              </div>
            )}
            {item.image_2 && (
              <div className="mt-3 cursor-pointer" onClick={() => setLightboxImg(item.image_2)}>
                <img src={item.image_2} alt="" className="w-24 h-24 rounded-lg object-cover border border-[#E5E7EB]" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2" data-testid="product-name">
              {item.name}
            </h1>
            <p className="font-heading text-2xl font-bold text-[#BA1826] mb-4" data-testid="product-price">
              ${item.price.toFixed(2)}
            </p>

            {item.description && (
              <p className="text-sm text-[#4B5563] leading-relaxed mb-6">{item.description}</p>
            )}

            {item.is_sold_out ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center mb-4">
                <p className="text-sm font-semibold text-red-700">Sold Out</p>
              </div>
            ) : (
              <>
                {/* Per-size apparel picker (stock-aware) */}
                {item.size_summary?.sizes?.length > 0 && (
                  <div className="mb-4" data-testid="size-picker">
                    <label className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wide mb-2 block">
                      Size
                      {!item.size_summary.sizes.some(s => s.in_stock) && (
                        <span className="ml-2 text-rose-600 normal-case">All sizes sold out</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {item.size_summary.sizes.map(s => {
                        const selected = selectedOptions.Size === s.size;
                        const disabled = !s.in_stock;
                        return (
                          <button key={s.size} type="button"
                            disabled={disabled}
                            onClick={() => !disabled && setSelectedOptions(prev => ({ ...prev, Size: s.size }))}
                            className={`relative px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                              disabled
                                ? 'border-[#E5E7EB] bg-[#FAFBFC] text-[#9CA3AF] cursor-not-allowed line-through'
                                : selected
                                  ? 'border-[#BA1826] bg-[#FEF2F2] text-[#BA1826]'
                                  : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:border-[#BA1826]/40'
                            }`}
                            title={disabled ? `${s.size} — sold out` : `${s.size} (${s.stock} available)`}
                            data-testid={`size-option-${s.size}`}>
                            {s.size}
                            {!disabled && s.stock <= 3 && (
                              <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[8px] px-1 rounded-full">
                                {s.stock}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Freeform variations (Color/Style/etc.) — skip Size when per-size stock is on */}
                {variations
                  .filter(v => !(item.size_summary?.sizes?.length && v.name?.trim().toLowerCase() === 'size'))
                  .map(v => (
                  <div key={v.name} className="mb-4" data-testid={`variation-${v.name}`}>
                    <label className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wide mb-2 block">
                      {v.name}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {(v.options || []).map(opt => (
                        <button key={opt} type="button"
                          onClick={() => setSelectedOptions(prev => ({ ...prev, [v.name]: opt }))}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            selectedOptions[v.name] === opt
                              ? 'border-[#BA1826] bg-[#FEF2F2] text-[#BA1826]'
                              : 'border-[#E5E7EB] bg-white text-[#4B5563] hover:border-[#BA1826]/40'
                          }`}
                          data-testid={`option-${v.name}-${opt}`}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Quantity */}
                <div className="mb-6">
                  <label className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wide mb-2 block">Quantity</label>
                  <div className="flex items-center border border-[#E5E7EB] rounded-lg bg-white w-fit">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-[#4B5563] hover:bg-[#F8F9FA]" data-testid="qty-minus">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-4 py-2 font-semibold text-sm min-w-[40px] text-center" data-testid="qty-display">{quantity}</span>
                    <button onClick={() => setQuantity(Math.min(maxQty, quantity + 1))}
                      className="px-3 py-2 text-[#4B5563] hover:bg-[#F8F9FA]" data-testid="qty-plus">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {item.is_low_stock && <p className="text-xs text-amber-600 mt-1">Only {item.remaining} left!</p>}
                </div>

                {/* Buy Button */}
                {!showCheckout ? (
                  <button onClick={() => setShowCheckout(true)}
                    className="w-full btn-primary text-sm py-3.5 flex items-center justify-center gap-2"
                    data-testid="buy-now-btn">
                    <ShoppingBag className="w-4 h-4" />
                    Buy Now — ${(item.price * quantity).toFixed(2)}
                  </button>
                ) : (
                  /* Shipping Form */
                  <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-3" data-testid="shipping-form">
                    <div className="flex items-center gap-2 mb-1">
                      <Truck className="w-4 h-4 text-[#BA1826]" />
                      <h3 className="font-heading font-bold text-sm">Shipping Details</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Full Name *</label>
                        <input type="text" value={shipping.name} onChange={(e) => setShipping(s => ({...s, name: e.target.value}))}
                          className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                          data-testid="shipping-name" />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Email *</label>
                        <input type="email" value={shipping.email} onChange={(e) => setShipping(s => ({...s, email: e.target.value}))}
                          className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none ${shipping.email && !isValidEmail(shipping.email) ? 'border-red-400 focus:border-red-500' : 'border-[#E5E7EB] focus:border-[#BA1826]'}`}
                          data-testid="shipping-email" />
                        {shipping.email && !isValidEmail(shipping.email) && (
                          <p className="text-[9px] text-red-500 mt-0.5">{getEmailError(shipping.email)}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Phone (optional)</label>
                      <input type="tel" value={shipping.phone} onChange={(e) => setShipping(s => ({...s, phone: e.target.value}))}
                        className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                        data-testid="shipping-phone" />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Street Address *</label>
                      <input type="text" value={shipping.address} onChange={(e) => setShipping(s => ({...s, address: e.target.value}))}
                        className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                        data-testid="shipping-address" />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Apartment / Suite (optional)</label>
                      <input type="text" value={shipping.apartment} onChange={(e) => setShipping(s => ({...s, apartment: e.target.value}))}
                        className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                        data-testid="shipping-apt" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">City *</label>
                        <input type="text" value={shipping.city} onChange={(e) => setShipping(s => ({...s, city: e.target.value}))}
                          className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                          data-testid="shipping-city" />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">State *</label>
                        <input type="text" value={shipping.state} onChange={(e) => setShipping(s => ({...s, state: e.target.value}))}
                          className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                          data-testid="shipping-state" />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">ZIP *</label>
                        <input type="text" value={shipping.zip} onChange={(e) => setShipping(s => ({...s, zip: e.target.value}))}
                          className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                          data-testid="shipping-zip" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Order Notes (optional)</label>
                      <textarea value={shipping.notes} onChange={(e) => setShipping(s => ({...s, notes: e.target.value}))}
                        rows={2} placeholder="Special instructions..."
                        className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none resize-none"
                        data-testid="shipping-notes" />
                    </div>

                    <MerchTransparencyNotice variant="checkout" />

                    {/* Order Summary */}
                    <div className="bg-[#F8F9FA] rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[#4B5563]">{item.name} x {quantity}</p>
                        {Object.entries(selectedOptions).map(([k, v]) => (
                          <p key={k} className="text-[10px] text-[#9CA3AF]">{k}: {v}</p>
                        ))}
                      </div>
                      <p className="font-heading text-lg font-bold text-[#0A0A0A]">${(item.price * quantity).toFixed(2)}</p>
                    </div>

                    <button onClick={handleCheckout} disabled={processing}
                      className="w-full btn-primary text-sm py-3.5 flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="checkout-submit-btn">
                      {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                      {processing ? 'Processing...' : `Pay $${(item.price * quantity).toFixed(2)}`}
                    </button>
                    <button onClick={() => setShowCheckout(false)} className="w-full text-xs text-[#4B5563] text-center py-1">
                      Back to product
                    </button>
                  </div>
                )}
              </>
            )}

            <div className="mt-4">
              <MerchTransparencyNotice variant="product" />
            </div>
          </div>
        </div>
      </div>
    </div>
    {lightboxImg && <ImageLightbox src={lightboxImg} alt={item.name} onClose={() => setLightboxImg(null)} />}
    </>
  );
};

export default ProductPage;
